/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import swal from "sweetalert";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Chip from "@mui/material/Chip";
import Badge from "@mui/material/Badge";
import CircularProgress from "@mui/material/CircularProgress";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Icons
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PersonIcon from "@mui/icons-material/Person";

import { logoutUser } from "actions/AuthAction";
import { useNavigate } from "react-router-dom";
import { API_ESA_OFFICE, API_ESA_OFFICE_UPLOAD } from "utils";

// Helper untuk format datetime
function formatDateTime(dateStr) {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  if (isNaN(date)) return "-";
  return date.toLocaleString("id-ID", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Helper untuk memotong nama dinas menjadi 3 kata
function truncateDinasName(nama) {
  if (!nama) return "";
  const words = nama.trim().split(/\s+/);
  if (words.length <= 3) return nama;
  return words.slice(0, 3).join(" ") + "...";
}

function DaftarEvents() {
  const [eventsList, setEventsList] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [openListDetailDialog, setOpenListDetailDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingDetailId, setEditingDetailId] = useState(null);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [selectedEventForList, setSelectedEventForList] = useState(null);
  const [detailEventsList, setDetailEventsList] = useState([]);
  const [loadingDetailEvents, setLoadingDetailEvents] = useState(false);
  const [detailEventsTooltip, setDetailEventsTooltip] = useState({});
  const [dinasId, setDinasId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [userStatus, setUserStatus] = useState("");
  const [userNamaDinas, setUserNamaDinas] = useState("");
  const [dinasList, setDinasList] = useState([]);
  const [dinasListFull, setDinasListFull] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submittingDetail, setSubmittingDetail] = useState(false);
  const [editingEventData, setEditingEventData] = useState(null);
  const [tahun, setTahun] = useState(new Date().getFullYear());
  /** null = semua bulan (default saat load pertama) */
  const [bulan, setBulan] = useState(null);
  const navigate = useNavigate();

  // Mobile detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [formData, setFormData] = useState({
    kegiatan: "",
    scope: "internal",
    status_event: "satu_kali",
    cek_ip_address: "ya",
    cek_koordinat: "tidak",
    target_dinas_ids: [], // Array of dinas_id untuk evaluasi/asistensi
    // Detail event
    detail_kegiatan: "",
    waktu_mulai: "",
    waktu_finish: "",
    tempat: "",
  });

  const [detailFormData, setDetailFormData] = useState({
    detail_kegiatan: "",
    waktu_mulai: "",
    waktu_finish: "",
    tempat: "",
    dinas_id: null, // Untuk evaluasi/asistensi
  });

  const [selectedEventForDetail, setSelectedEventForDetail] = useState(null);
  const [targetDinasForDetail, setTargetDinasForDetail] = useState([]);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setDinasId(user.dinas_id);
      setUserId(user.uid || user.user_id);
      setUserStatus(user.status);
      setUserNamaDinas(user.nama_dinas || "");

      // Jika nama_dinas tidak ada, fetch dari API
      if (!user.nama_dinas && user.dinas_id) {
        axios
          .get(`${API_ESA_OFFICE}dinas/${user.dinas_id}`)
          .then((res) => {
            if (res.data && res.data.nama_dinas) {
              setUserNamaDinas(res.data.nama_dinas);
            }
          })
          .catch((err) => {
            console.error("Error fetching dinas:", err);
          });
      }
    } else {
      logoutUser(navigate);
    }
  }, []);

  // Fetch list dinas untuk autocomplete
  useEffect(() => {
    const fetchDinasList = async () => {
      try {
        const response = await axios.get(`${API_ESA_OFFICE}dinas`);
        const dinasOptions = (response.data || []).map(
          (dinas) => dinas.nama_dinas
        );
        setDinasList(dinasOptions);
        setDinasListFull(response.data || []);
      } catch (error) {
        console.error("Error fetching dinas list:", error);
      }
    };
    fetchDinasList();
  }, []);

  const fetchEvents = useCallback(async () => {
    if (!dinasId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      let url = `${API_ESA_OFFICE}events?dinasId=${dinasId}`;
      if (tahun) {
        url += `&tahun=${tahun}`;
      }
      if (bulan) {
        url += `&bulan=${bulan}`;
      }
      const response = await axios.get(url);

      // Validasi response
      if (!response || !response.data) {
        console.warn("Response tidak valid:", response);
        setEventsList([]);
        setLoading(false);
        return;
      }

      const events = Array.isArray(response.data) ? response.data : [];

      // Fetch jumlah target dinas untuk event evaluasi/asistensi
      const eventsWithTargetDinas = await Promise.all(
        events.map(async (event) => {
          // Pastikan event valid
          if (!event || !event.id) {
            return event;
          }

          if (
            event.status_event === "evaluasi" ||
            event.status_event === "asistensi"
          ) {
            try {
              const targetDinasResponse = await axios.get(
                `${API_ESA_OFFICE}events/${event.id}/target-dinas`
              );
              event.jumlah_target_dinas = Array.isArray(
                targetDinasResponse.data
              )
                ? targetDinasResponse.data.length
                : 0;
            } catch (error) {
              console.error(
                `Error fetching target dinas for event ${event.id}:`,
                error
              );
              event.jumlah_target_dinas = 0;
            }
          } else {
            // Pastikan property ada untuk event non-evaluasi/asistensi
            event.jumlah_target_dinas = 0;
          }
          return event;
        })
      );

      setEventsList(eventsWithTargetDinas);
    } catch (error) {
      console.error("Error fetching events:", error);
      // Set empty array untuk mencegah crash
      setEventsList([]);

      // Hanya tampilkan error jika bukan dari user action (seperti setelah submit)
      // Untuk menghindari double alert
      if (error.response && error.response.status !== 404) {
        swal({
          title: "Error",
          text: error.response?.data?.message || "Gagal mengambil data events",
          icon: "error",
        });
      }
    } finally {
      setLoading(false);
    }
  }, [dinasId, tahun, bulan]);

  useEffect(() => {
    if (dinasId) {
      fetchEvents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dinasId, tahun, bulan]);

  const handleOpenDialog = async () => {
    setEditingId(null);
    setEditingEventData(null);
    setFormData({
      kegiatan: "",
      scope: "internal",
      status_event: "satu_kali",
      cek_ip_address: "ya",
      cek_koordinat: "tidak",
      target_dinas_ids: [],
      detail_kegiatan: "",
      waktu_mulai: "",
      waktu_finish: "",
      tempat: userNamaDinas || "",
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingId(null);
    setEditingEventData(null);
    setFormData({
      kegiatan: "",
      scope: "internal",
      status_event: "satu_kali",
      cek_ip_address: "ya",
      cek_koordinat: "tidak",
      target_dinas_ids: [],
      detail_kegiatan: "",
      waktu_mulai: "",
      waktu_finish: "",
      tempat: "",
    });
    setSubmitting(false);
  };

  const handleEdit = useCallback(async (event) => {
    setEditingId(event.id);

    // Fetch target dinas jika status evaluasi atau asistensi
    let targetDinasIds = [];
    if (
      event.status_event === "evaluasi" ||
      event.status_event === "asistensi"
    ) {
      try {
        const response = await axios.get(
          `${API_ESA_OFFICE}events/${event.id}/target-dinas`
        );
        targetDinasIds = (response.data || []).map((item) => item.dinas_id);
      } catch (error) {
        console.error("Error fetching target dinas:", error);
        targetDinasIds = [];
      }
    }

    setFormData({
      kegiatan: event.kegiatan || "",
      scope: event.scope || "internal",
      status_event: event.status_event || "satu_kali",
      cek_ip_address: event.cek_ip_address || "ya",
      cek_koordinat: event.cek_koordinat || "tidak",
      target_dinas_ids: targetDinasIds,
    });
    setOpenDialog(true);
  }, []);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleScopeChange = useCallback((event, newValue) => {
    setFormData((prev) => ({
      ...prev,
      scope: newValue || "internal",
    }));
  }, []);

  const handleStatusEventChange = useCallback((event, newValue) => {
    setFormData((prev) => ({
      ...prev,
      status_event: newValue || "satu_kali",
    }));
  }, []);

  const handleDetailKegiatanChange = useCallback((e) => {
    setFormData((prev) => ({
      ...prev,
      detail_kegiatan: e.target.value,
    }));
  }, []);

  const handleDetailFormChange = useCallback(
    (field) => (e) => {
      setDetailFormData((prev) => ({
        ...prev,
        [field]: e.target.value,
      }));
    },
    []
  );

  const handleTempatChange = useCallback((event, newValue) => {
    setFormData((prev) => ({
      ...prev,
      tempat: typeof newValue === "string" ? newValue : newValue || "",
    }));
  }, []);

  const handleDetailTempatChange = useCallback((event, newValue) => {
    setDetailFormData((prev) => ({
      ...prev,
      tempat: typeof newValue === "string" ? newValue : newValue || "",
    }));
  }, []);

  const scopeOptions = ["internal", "pemda", "publik"];
  const statusEventOptions = ["satu_kali", "rutin", "evaluasi", "asistensi"];

  const handleSubmit = async () => {
    if (submitting) return; // Prevent double submission

    // Validasi untuk create dan edit
    if (!formData.kegiatan || formData.kegiatan.trim() === "") {
      swal({
        title: "Error",
        text: "Nama kegiatan harus diisi",
        icon: "error",
      });
      return;
    }

    if (!formData.scope) {
      swal({
        title: "Error",
        text: "Scope harus dipilih",
        icon: "error",
      });
      return;
    }

    if (!formData.status_event) {
      swal({
        title: "Error",
        text: "Status event harus dipilih",
        icon: "error",
      });
      return;
    }

    // Validasi khusus untuk create (tambah event baru)
    // Skip validasi detail event jika status adalah evaluasi atau asistensi
    const skipDetailEvent =
      formData.status_event === "evaluasi" ||
      formData.status_event === "asistensi";

    if (!editingId && !skipDetailEvent) {
      if (!formData.waktu_mulai || formData.waktu_mulai.trim() === "") {
        swal({
          title: "Error",
          text: "Waktu mulai harus diisi",
          icon: "error",
        });
        return;
      }

      // Validasi waktu finish harus setelah waktu mulai (jika diisi)
      if (formData.waktu_finish && formData.waktu_finish.trim() !== "") {
        const waktuMulai = new Date(formData.waktu_mulai);
        const waktuFinish = new Date(formData.waktu_finish);
        if (waktuFinish < waktuMulai) {
          swal({
            title: "Error",
            text: "Waktu selesai harus setelah waktu mulai",
            icon: "error",
          });
          return;
        }
      }
    }

    setSubmitting(true);
    try {
      const isEvaluasiOrAsistensi =
        formData.status_event === "evaluasi" ||
        formData.status_event === "asistensi";

      if (editingId) {
        // Update - bidang_id dan dinas_id tidak diubah, diambil dari user di backend
        const updatePayload = {
          dinas_id: dinasId,
          kegiatan: formData.kegiatan,
          scope: formData.scope,
          status_event: formData.status_event,
          cek_ip_address: formData.cek_ip_address || "ya",
          cek_koordinat: formData.cek_koordinat || "tidak",
        };

        // Hanya kirim target_dinas_ids jika status evaluasi atau asistensi
        if (isEvaluasiOrAsistensi) {
          updatePayload.target_dinas_ids = formData.target_dinas_ids || [];
        }

        await axios.put(`${API_ESA_OFFICE}events/${editingId}`, updatePayload);

        // Tutup dialog terlebih dahulu
        handleCloseDialog();

        // Fetch events sebelum menampilkan alert
        await fetchEvents();

        // Tampilkan alert setelah fetch selesai
        swal({
          title: "Berhasil!",
          text: "Event berhasil diupdate",
          icon: "success",
          button: "OK",
        });
      } else {
        // Create event + detail event
        // 1. Create event
        const createPayload = {
          dinas_id: dinasId,
          kegiatan: formData.kegiatan,
          scope: formData.scope,
          status_event: formData.status_event,
          cek_ip_address: formData.cek_ip_address || "ya",
          cek_koordinat: formData.cek_koordinat || "tidak",
          id_pembuat: userId,
        };

        // Hanya kirim target_dinas_ids jika status evaluasi atau asistensi
        if (isEvaluasiOrAsistensi) {
          createPayload.target_dinas_ids = formData.target_dinas_ids || [];
        }

        const eventResponse = await axios.post(
          `${API_ESA_OFFICE}events`,
          createPayload
        );

        // Validasi response
        if (!eventResponse || !eventResponse.data) {
          throw new Error("Response tidak valid dari server");
        }

        const eventId = eventResponse.data.id || eventResponse.data.event?.id;

        if (!eventId) {
          throw new Error("ID event tidak ditemukan dalam response");
        }

        // 2. Create detail event (hanya jika status bukan evaluasi atau asistensi)
        if (!isEvaluasiOrAsistensi && formData.waktu_mulai) {
          try {
            await axios.post(`${API_ESA_OFFICE}events/${eventId}/detail`, {
              detail_kegiatan: formData.detail_kegiatan || null,
              waktu_mulai: formData.waktu_mulai,
              waktu_finish: formData.waktu_finish || null,
              tempat: formData.tempat || null,
            });
          } catch (detailError) {
            console.error("Error creating detail event:", detailError);
            // Tetap lanjutkan meskipun detail event gagal dibuat
          }
        }

        // Tutup dialog terlebih dahulu
        handleCloseDialog();

        // Fetch events sebelum menampilkan alert
        await fetchEvents();

        // Tampilkan alert setelah fetch selesai
        swal({
          title: "Berhasil!",
          text: isEvaluasiOrAsistensi
            ? "Event berhasil ditambahkan"
            : "Event dan detail event berhasil ditambahkan",
          icon: "success",
          button: "OK",
        });
      }
    } catch (error) {
      console.error("Error saving event:", error);
      // Pastikan dialog ditutup meskipun ada error
      handleCloseDialog();

      swal({
        title: "Error!",
        text:
          error.response?.data?.message ||
          error.message ||
          "Gagal menyimpan event",
        icon: "error",
        button: "OK",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = useCallback(
    async (id) => {
      swal({
        title: "Apakah Anda yakin?",
        text: "Data event akan dihapus secara permanen!",
        icon: "warning",
        buttons: ["Batal", "Ya, Hapus!"],
        dangerMode: true,
      }).then(async (willDelete) => {
        if (willDelete) {
          try {
            await axios.delete(`${API_ESA_OFFICE}events/${id}`);
            swal({
              title: "Berhasil!",
              text: "Event berhasil dihapus",
              icon: "success",
              button: "OK",
            });
            fetchEvents();
          } catch (error) {
            swal({
              title: "Error!",
              text:
                error.response?.data?.message ||
                "Terjadi kesalahan saat menghapus event",
              icon: "error",
              button: "OK",
            });
          }
        }
      });
    },
    [fetchEvents]
  );

  const handleOpenDetailDialog = useCallback(
    async (event) => {
      const isEvaluasiOrAsistensi =
        event.status_event === "evaluasi" || event.status_event === "asistensi";

      // Untuk rutin, hanya pembuat yang bisa menambah detail
      if (event.status_event === "rutin") {
        const isPembuat =
          userId &&
          event.id_pembuat &&
          parseInt(userId) === parseInt(event.id_pembuat);

        if (!isPembuat) {
          swal({
            title: "Akses Ditolak",
            text: "Anda bukan pembuat event ini, jadi tidak dapat menambah daftar event",
            icon: "warning",
            button: "OK",
          });
          return;
        }
      }

      setSelectedEventId(event.id);
      setSelectedEventForDetail(event);
      setEditingDetailId(null);

      // Fetch target dinas jika evaluasi/asistensi
      let targetDinas = [];
      if (isEvaluasiOrAsistensi) {
        try {
          // Fetch target dinas dari event
          const targetDinasResponse = await axios.get(
            `${API_ESA_OFFICE}events/${event.id}/target-dinas`
          );
          // Filter hanya dinas yang memiliki nama_dinas (valid dinas)
          const allTargetDinas = (targetDinasResponse.data || []).filter(
            (d) => d.dinas_id && d.nama_dinas
          );

          // Fetch detail events yang sudah ada untuk mendapatkan dinas_id yang sudah digunakan
          const detailEventsResponse = await axios.get(
            `${API_ESA_OFFICE}events/${event.id}/detail`
          );
          const existingDetailEvents = detailEventsResponse.data || [];

          // Ambil daftar dinas_id yang sudah memiliki detail event
          // Filter hanya detail events yang memiliki dinas_id (untuk evaluasi/asistensi)
          const usedDinasIds = existingDetailEvents
            .map((de) => de.dinas_id)
            .filter((dinasId) => dinasId !== null && dinasId !== undefined);

          // Filter target dinas untuk mengecualikan dinas yang sudah memiliki detail event
          // Tujuannya: mencegah duplikasi detail event untuk dinas yang sama
          targetDinas = allTargetDinas.filter(
            (d) => !usedDinasIds.includes(d.dinas_id)
          );
        } catch (error) {
          console.error("Error fetching target dinas or detail events:", error);
          targetDinas = [];
        }
      }
      setTargetDinasForDetail(targetDinas);

      setDetailFormData({
        detail_kegiatan: "",
        waktu_mulai: "",
        waktu_finish: "",
        tempat: userNamaDinas || "",
        dinas_id: null,
      });
      setOpenDetailDialog(true);
    },
    [userId, userNamaDinas]
  );

  const handleOpenListDetailDialog = useCallback(async (event) => {
    setSelectedEventForList(event);
    setLoadingDetailEvents(true);
    setOpenListDetailDialog(true);

    try {
      const response = await axios.get(
        `${API_ESA_OFFICE}events/${event.id}/detail`
      );
      setDetailEventsList(response.data || []);
    } catch (error) {
      console.error("Error fetching detail events:", error);
      swal({
        title: "Error",
        text: "Gagal mengambil data detail events",
        icon: "error",
      });
      setDetailEventsList([]);
    } finally {
      setLoadingDetailEvents(false);
    }
  }, []);

  const handleCloseListDetailDialog = () => {
    setOpenListDetailDialog(false);
    setSelectedEventForList(null);
    setDetailEventsList([]);
  };

  const handleCloseDetailDialog = () => {
    setOpenDetailDialog(false);
    setEditingDetailId(null);
    setSelectedEventId(null);
    setSelectedEventForDetail(null);
    setTargetDinasForDetail([]);
    setDetailFormData({
      detail_kegiatan: "",
      waktu_mulai: "",
      waktu_finish: "",
      tempat: "",
      dinas_id: null,
    });
    setSubmittingDetail(false);
  };

  const handleSubmitDetailEvent = async () => {
    if (submittingDetail) return;

    const isEvaluasiOrAsistensi =
      selectedEventForDetail &&
      (selectedEventForDetail.status_event === "evaluasi" ||
        selectedEventForDetail.status_event === "asistensi");

    // Validasi waktu mulai
    if (
      !detailFormData.waktu_mulai ||
      detailFormData.waktu_mulai.trim() === ""
    ) {
      swal({
        title: "Error",
        text: "Waktu mulai harus diisi",
        icon: "error",
      });
      return;
    }

    // Validasi dinas_id untuk evaluasi/asistensi
    if (isEvaluasiOrAsistensi && !detailFormData.dinas_id) {
      swal({
        title: "Error",
        text: "Dinas harus dipilih",
        icon: "error",
      });
      return;
    }

    // Validasi waktu finish harus setelah waktu mulai (jika diisi)
    if (
      detailFormData.waktu_finish &&
      detailFormData.waktu_finish.trim() !== ""
    ) {
      const waktuMulai = new Date(detailFormData.waktu_mulai);
      const waktuFinish = new Date(detailFormData.waktu_finish);
      if (waktuFinish < waktuMulai) {
        swal({
          title: "Error",
          text: "Waktu selesai harus setelah waktu mulai",
          icon: "error",
        });
        return;
      }
    }

    setSubmittingDetail(true);
    try {
      const payload = {
        detail_kegiatan: detailFormData.detail_kegiatan || null,
        waktu_mulai: detailFormData.waktu_mulai,
        waktu_finish: detailFormData.waktu_finish || null,
        tempat: detailFormData.tempat || null,
      };

      // Tambahkan dinas_id dan id_pembuat jika evaluasi/asistensi
      if (isEvaluasiOrAsistensi) {
        payload.dinas_id = detailFormData.dinas_id;
        payload.id_pembuat = userId; // Ambil dari localStorage user.uid
      }

      if (editingDetailId) {
        // Update detail event
        await axios.put(
          `${API_ESA_OFFICE}events/${selectedEventId}/detail/${editingDetailId}`,
          payload
        );
        swal({
          title: "Berhasil!",
          text: "Detail event berhasil diupdate",
          icon: "success",
          button: "OK",
        });
      } else {
        // Create detail event
        await axios.post(
          `${API_ESA_OFFICE}events/${selectedEventId}/detail`,
          payload
        );
        swal({
          title: "Berhasil!",
          text: "Detail event berhasil ditambahkan",
          icon: "success",
          button: "OK",
        });
      }

      handleCloseDetailDialog();
      fetchEvents();
    } catch (error) {
      console.error("Error saving detail event:", error);
      swal({
        title: "Error!",
        text: error.response?.data?.message || "Gagal menyimpan detail event",
        icon: "error",
        button: "OK",
      });
    } finally {
      setSubmittingDetail(false);
    }
  };

  const getScopeLabel = (scope) => {
    switch (scope) {
      case "internal":
        return "Internal";
      case "pemda":
        return "Pemda";
      case "publik":
        return "Publik";
      default:
        return scope;
    }
  };

  const getScopeColor = (scope) => {
    switch (scope) {
      case "internal":
        return "info";
      case "pemda":
        return "warning";
      case "publik":
        return "success";
      default:
        return "default";
    }
  };

  const getStatusEventLabel = (status) => {
    switch (status) {
      case "satu_kali":
        return "Satu Kali";
      case "rutin":
        return "Rutin";
      case "evaluasi":
        return "Evaluasi";
      case "asistensi":
        return "Asistensi";
      default:
        return status;
    }
  };

  const getStatusEventColor = (status) => {
    switch (status) {
      case "satu_kali":
        return "info";
      case "rutin":
        return "warning";
      case "evaluasi":
        return "success";
      case "asistensi":
        return "secondary";
      default:
        return "default";
    }
  };

  // Hanya admin atau super admin yang bisa melakukan CRUD
  const isAdmin = true;

  // Gunakan useMemo untuk columns agar reference tetap stabil
  // Ini mencegah DataTable reset pagination saat re-render
  const columns = useMemo(
    () => [
      { Header: "No", accessor: "no", width: "5%" },
      {
        Header: "Kegiatan",
        accessor: "kegiatan",
        width: "20%",
        Cell: ({ value }) => (
          <MDBox
            sx={{
              whiteSpace: "normal",
              wordWrap: "break-word",
              maxWidth: "300px",
              py: 1,
            }}
          >
            <MDTypography variant="caption" color="text">
              {value || "-"}
            </MDTypography>
          </MDBox>
        ),
      },
      {
        Header: "Bidang",
        accessor: "nama_bidang",
        width: "15%",
        Cell: ({ value }) => (
          <MDBox
            sx={{
              whiteSpace: "normal",
              wordWrap: "break-word",
              maxWidth: "200px",
              py: 1,
            }}
          >
            <MDTypography variant="caption" color="text">
              {value || "-"}
            </MDTypography>
          </MDBox>
        ),
      },
      {
        Header: "Scope",
        accessor: "scope",
        width: "10%",
        Cell: ({ value }) => (
          <Chip
            label={getScopeLabel(value)}
            color={getScopeColor(value)}
            size="small"
            variant="filled"
          />
        ),
      },
      {
        Header: "Status Event",
        accessor: "status_event",
        width: "10%",
        Cell: ({ value, row }) => {
          const isEvaluasiOrAsistensi =
            value === "evaluasi" || value === "asistensi";
          const jumlahTargetDinas = row.original.jumlah_target_dinas || 0;

          return (
            <MDBox>
              <Chip
                label={getStatusEventLabel(value)}
                color={getStatusEventColor(value)}
                size="small"
                variant="filled"
              />
              {isEvaluasiOrAsistensi && jumlahTargetDinas > 0 && (
                <MDTypography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.5 }}
                >
                  {jumlahTargetDinas} dinas
                </MDTypography>
              )}
            </MDBox>
          );
        },
      },
      {
        Header: "Jumlah Event",
        accessor: "jumlah_detail_events",
        width: "10%",
        Cell: ({ value, row }) => {
          const handleMouseEnter = async () => {
            // Jika belum ada data tooltip untuk event ini, fetch
            if (!detailEventsTooltip[row.original.id] && row.original.id) {
              try {
                const response = await axios.get(
                  `${API_ESA_OFFICE}events/${row.original.id}/detail`
                );
                const detailEvents = response.data || [];
                setDetailEventsTooltip((prev) => ({
                  ...prev,
                  [row.original.id]: detailEvents,
                }));
              } catch (error) {
                console.error(
                  "Error fetching detail events for tooltip:",
                  error
                );
              }
            }
          };

          const tooltipData = detailEventsTooltip[row.original.id] || [];
          const tooltipTitle =
            tooltipData.length > 0
              ? tooltipData
                  .map((de, idx) => {
                    const dinasInfo =
                      de.dinas_id && de.nama_dinas ? ` (${de.nama_dinas})` : "";
                    return `${idx + 1}. ${
                      de.detail_kegiatan || "-"
                    }${dinasInfo}`;
                  })
                  .join("\n")
              : "Klik untuk melihat detail events";

          return (
            <Tooltip
              title={
                <div style={{ whiteSpace: "pre-line" }}>{tooltipTitle}</div>
              }
              arrow
              onOpen={handleMouseEnter}
            >
              <Chip
                label={value || 0}
                color="info"
                size="small"
                onClick={() => handleOpenListDetailDialog(row.original)}
                sx={{
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "info.dark",
                  },
                }}
              />
            </Tooltip>
          );
        },
      },
      {
        Header: "Pembuat",
        accessor: "nama_pembuat",
        width: "15%",
        Cell: ({ row }) => (
          <MDBox display="flex" alignItems="center" gap={1}>
            {row.original.uid_RSS_pembuat ? (
              <img
                src={`https://smartdh.rotendaokab.go.id/uploads/${row.original.uid_RSS_pembuat}.jpg`}
                alt={row.original.nama_pembuat || "User"}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            ) : (
              <MDBox
                display="flex"
                alignItems="center"
                justifyContent="center"
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  backgroundColor: "grey.200",
                  color: "grey.500",
                }}
              >
                <PersonIcon sx={{ fontSize: 20 }} />
              </MDBox>
            )}
            <MDTypography variant="caption" color="text">
              {row.original.nama_pembuat || "-"}
            </MDTypography>
          </MDBox>
        ),
      },
      {
        Header: "Link",
        accessor: "link",
        width: "15%",
        Cell: ({ value, row }) => (
          <MDBox>
            {value ? (
              <MDBox display="flex" alignItems="center" gap={1}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    window.open(value, "_blank");
                  }}
                  sx={{
                    color: "#000000",
                    borderColor: "#000000",
                    "&:hover": {
                      borderColor: "#000000",
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                    },
                  }}
                >
                  Open Link
                </Button>
              </MDBox>
            ) : (
              <MDTypography variant="caption" color="text.secondary">
                -
              </MDTypography>
            )}
          </MDBox>
        ),
      },
      {
        Header: "QR Code",
        accessor: "qrcode",
        width: "10%",
        Cell: ({ row }) => (
          <MDBox>
            {row.original.qr_code ? (
              <img
                src={`https://esa-office.rotendaokab.go.id${
                  row.original.qr_code.startsWith("/")
                    ? row.original.qr_code
                    : "/" + row.original.qr_code
                }`}
                alt="QR Code"
                style={{ width: 50, height: 50, cursor: "pointer" }}
                onClick={() => {
                  // Open QR code in new window
                  const qrPath = row.original.qr_code.startsWith("/")
                    ? row.original.qr_code
                    : "/" + row.original.qr_code;
                  window.open(
                    `https://esa-office.rotendaokab.go.id${qrPath}`,
                    "_blank"
                  );
                }}
              />
            ) : (
              <MDTypography variant="caption" color="text.secondary">
                -
              </MDTypography>
            )}
          </MDBox>
        ),
      },
      {
        Header: "Tambah Detail",
        accessor: "tambah_list",
        width: "15%",
        Cell: ({ row }) => {
          const isRutin = row.original.status_event === "rutin";
          const isEvaluasiOrAsistensi =
            row.original.status_event === "evaluasi" ||
            row.original.status_event === "asistensi";

          return (
            <MDBox>
              {(isRutin || isEvaluasiOrAsistensi) && (
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => handleOpenDetailDialog(row.original)}
                  sx={{
                    backgroundColor: "#000000",
                    color: "#ffffff",
                    "&:hover": {
                      backgroundColor: "#333333",
                    },
                  }}
                >
                  Tambah Detail
                </Button>
              )}
            </MDBox>
          );
        },
      },
      {
        Header: "Aksi",
        accessor: "aksi",
        width: "10%",
        Cell: ({ row }) => {
          const isPembuat =
            userId &&
            row.original.id_pembuat &&
            parseInt(userId) === parseInt(row.original.id_pembuat);
          return (
            <MDBox display="flex" gap={1}>
              {isPembuat ? (
                <>
                  <Tooltip title="Edit">
                    <IconButton
                      color="info"
                      size="small"
                      onClick={() => handleEdit(row.original)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Hapus">
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => handleDelete(row.original.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              ) : (
                <Tooltip title="Lihat">
                  <IconButton
                    color="info"
                    size="small"
                    onClick={() => handleOpenListDetailDialog(row.original)}
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </MDBox>
          );
        },
      },
    ],
    [
      userId,
      detailEventsTooltip,
      handleOpenListDetailDialog,
      handleOpenDetailDialog,
      handleEdit,
      handleDelete,
    ]
  );

  // Gunakan useMemo untuk mencegah re-create rows setiap render
  // Ini akan menjaga pagination state tetap stabil
  const rows = useMemo(() => {
    return eventsList.map((event, index) => ({
      id: event.id,
      no: index + 1,
      kegiatan: event.kegiatan,
      nama_bidang: event.nama_bidang || "-",
      scope: event.scope,
      status_event: event.status_event,
      jumlah_detail_events: event.jumlah_detail_events || 0,
      jumlah_target_dinas: event.jumlah_target_dinas || 0,
      nama_pembuat: event.nama_pembuat || "-",
      id_pembuat: event.id_pembuat,
      uid_RSS_pembuat: event.uid_RSS_pembuat,
      link: event.link,
      qr_code: event.qr_code,
    }));
  }, [eventsList]);

  // Gunakan useMemo untuk table object agar reference tetap stabil
  // Ini mencegah DataTable reset pagination saat re-render
  const tableData = useMemo(
    () => ({
      columns,
      rows,
    }),
    [columns, rows]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  Daftar Events/Kegiatan
                </MDTypography>
                {isAdmin && (
                  <Button
                    variant="contained"
                    color="white"
                    startIcon={<AddIcon />}
                    onClick={handleOpenDialog}
                  >
                    Tambah Event
                  </Button>
                )}
              </MDBox>
              <MDBox pt={4} pb={3} px={3}>
                {/* Filter Tahun dan Bulan */}
                <Grid container spacing={2} mb={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete
                      options={(() => {
                        const currentYear = new Date().getFullYear();
                        const years = [];
                        // Mulai dari 2025 sampai current tahun
                        for (let i = 2025; i <= currentYear; i++) {
                          years.push({ label: i.toString(), value: i });
                        }
                        // Urutkan dari terbaru ke terlama (DESC)
                        return years.reverse();
                      })()}
                      value={
                        tahun ? { label: tahun.toString(), value: tahun } : null
                      }
                      onChange={(event, newValue) => {
                        if (newValue) {
                          setTahun(newValue.value);
                        }
                      }}
                      renderInput={(params) => (
                        <MDInput
                          {...params}
                          label="Tahun"
                          variant="standard"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete
                      options={[
                        { label: "Semua Bulan", value: "semua" },
                        { label: "Januari", value: 1 },
                        { label: "Februari", value: 2 },
                        { label: "Maret", value: 3 },
                        { label: "April", value: 4 },
                        { label: "Mei", value: 5 },
                        { label: "Juni", value: 6 },
                        { label: "Juli", value: 7 },
                        { label: "Agustus", value: 8 },
                        { label: "September", value: 9 },
                        { label: "Oktober", value: 10 },
                        { label: "November", value: 11 },
                        { label: "Desember", value: 12 },
                      ]}
                      value={
                        !bulan || bulan === "semua"
                          ? { label: "Semua Bulan", value: "semua" }
                          : {
                              label: [
                                "Januari",
                                "Februari",
                                "Maret",
                                "April",
                                "Mei",
                                "Juni",
                                "Juli",
                                "Agustus",
                                "September",
                                "Oktober",
                                "November",
                                "Desember",
                              ][bulan - 1],
                              value: bulan,
                            }
                      }
                      onChange={(event, newValue) => {
                        if (!newValue || newValue.value === "semua") {
                          setBulan(null);
                        } else {
                          setBulan(newValue.value);
                        }
                      }}
                      renderInput={(params) => (
                        <MDInput
                          {...params}
                          label="Bulan"
                          variant="standard"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                </Grid>
                {loading ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="200px"
                  >
                    <CircularProgress />
                    <MDTypography variant="body2" color="text" ml={2}>
                      Memuat data events...
                    </MDTypography>
                  </MDBox>
                ) : isMobile ? (
                  <MDBox display="flex" flexDirection="column" gap={1}>
                    {eventsList.length === 0 ? (
                      <MDBox
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        py={4}
                      >
                        <MDTypography variant="h6" color="text.secondary">
                          Tidak ada data events
                        </MDTypography>
                        <MDTypography variant="body2" color="text.secondary">
                          {isAdmin
                            ? "Klik tombol 'Tambah Event' untuk menambahkan event baru"
                            : "Belum ada events yang terdaftar"}
                        </MDTypography>
                      </MDBox>
                    ) : (
                      eventsList.map((event, index) => (
                        <Card key={event.id}>
                          <CardContent>
                            <MDBox>
                              <MDTypography variant="h6" fontWeight="bold">
                                {event.kegiatan}
                              </MDTypography>
                              {event.nama_bidang && (
                                <MDBox mt={1}>
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Bidang:
                                  </MDTypography>
                                  <MDTypography variant="body2" color="text">
                                    {event.nama_bidang}
                                  </MDTypography>
                                </MDBox>
                              )}
                              <MDBox
                                mt={1}
                                display="flex"
                                gap={1}
                                alignItems="center"
                                flexWrap="wrap"
                              >
                                <MDTypography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Scope:
                                </MDTypography>
                                <Chip
                                  label={getScopeLabel(event.scope)}
                                  color={getScopeColor(event.scope)}
                                  size="small"
                                  variant="filled"
                                />
                                <MDTypography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Status:
                                </MDTypography>
                                <Chip
                                  label={getStatusEventLabel(
                                    event.status_event
                                  )}
                                  color={getStatusEventColor(
                                    event.status_event
                                  )}
                                  size="small"
                                  variant="filled"
                                />
                              </MDBox>
                              {event.link && (
                                <MDBox mt={1}>
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Link Daftar Hadir:
                                  </MDTypography>
                                  <MDBox
                                    display="flex"
                                    gap={1}
                                    alignItems="center"
                                    mt={0.5}
                                    flexWrap="wrap"
                                  >
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => {
                                        window.open(event.link, "_blank");
                                      }}
                                      sx={{
                                        color: "#000000",
                                        borderColor: "#000000",
                                        "&:hover": {
                                          borderColor: "#000000",
                                          backgroundColor:
                                            "rgba(0, 0, 0, 0.04)",
                                        },
                                      }}
                                    >
                                      Open Link
                                    </Button>
                                  </MDBox>
                                </MDBox>
                              )}
                              {event.qr_code && (
                                <MDBox mt={1}>
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    QR Code:
                                  </MDTypography>
                                  <MDBox mt={0.5}>
                                    <img
                                      src={`https://esa-office.rotendaokab.go.id${
                                        event.qr_code.startsWith("/")
                                          ? event.qr_code
                                          : "/" + event.qr_code
                                      }`}
                                      alt="QR Code"
                                      style={{
                                        width: 100,
                                        height: 100,
                                        cursor: "pointer",
                                      }}
                                      onClick={() => {
                                        const qrPath = event.qr_code.startsWith(
                                          "/"
                                        )
                                          ? event.qr_code
                                          : "/" + event.qr_code;
                                        window.open(
                                          `https://esa-office.rotendaokab.go.id${qrPath}`,
                                          "_blank"
                                        );
                                      }}
                                    />
                                  </MDBox>
                                </MDBox>
                              )}
                              {event.jumlah_detail_events !== undefined && (
                                <MDBox mt={1}>
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Jumlah Event:
                                  </MDTypography>
                                  <MDBox mt={0.5}>
                                    <Chip
                                      label={event.jumlah_detail_events || 0}
                                      color="info"
                                      size="small"
                                      onClick={() =>
                                        handleOpenListDetailDialog(event)
                                      }
                                      sx={{
                                        cursor: "pointer",
                                        "&:hover": {
                                          backgroundColor: "info.dark",
                                        },
                                      }}
                                    />
                                  </MDBox>
                                </MDBox>
                              )}
                              {event.nama_pembuat && (
                                <MDBox mt={1}>
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Pembuat:
                                  </MDTypography>
                                  <MDTypography variant="body2" color="text">
                                    {event.nama_pembuat}
                                  </MDTypography>
                                </MDBox>
                              )}
                              {(event.status_event === "rutin" ||
                                event.status_event === "evaluasi" ||
                                event.status_event === "asistensi") && (
                                <MDBox mt={1}>
                                  <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() =>
                                      handleOpenDetailDialog(event)
                                    }
                                    sx={{
                                      backgroundColor: "#000000",
                                      color: "#ffffff",
                                      "&:hover": {
                                        backgroundColor: "#333333",
                                      },
                                    }}
                                  >
                                    Tambah Detail
                                  </Button>
                                </MDBox>
                              )}
                              <MDBox mt={2} display="flex" gap={1}>
                                {userId &&
                                event.id_pembuat &&
                                parseInt(userId) ===
                                  parseInt(event.id_pembuat) ? (
                                  <>
                                    <Tooltip title="Edit">
                                      <IconButton
                                        color="info"
                                        size="small"
                                        onClick={() => handleEdit(event)}
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Hapus">
                                      <IconButton
                                        color="error"
                                        size="small"
                                        onClick={() => handleDelete(event.id)}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </>
                                ) : (
                                  <Tooltip title="Lihat">
                                    <IconButton
                                      color="info"
                                      size="small"
                                      onClick={() =>
                                        handleOpenListDetailDialog(event)
                                      }
                                    >
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </MDBox>
                            </MDBox>
                          </CardContent>
                        </Card>
                      ))
                    )}
                  </MDBox>
                ) : (
                  <DataTable
                    table={tableData}
                    entriesPerPage={{
                      defaultValue: 10,
                      entries: [5, 10, 15, 20, 25],
                    }}
                    showTotalEntries={true}
                    isSorted={true}
                    noEndBorder
                    loading={loading}
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Dialog Form Tambah/Edit Event */}
      {isAdmin && (
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <MDTypography variant="h6">
                {editingId ? "Edit Event" : "Tambah Event Baru"}
              </MDTypography>
              <IconButton onClick={handleCloseDialog}>
                <CloseIcon />
              </IconButton>
            </MDBox>
          </DialogTitle>
          <DialogContent>
            <MDBox pt={2} pb={3} px={3}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <MDInput
                    label="Nama Kegiatan"
                    name="kegiatan"
                    value={formData.kegiatan}
                    onChange={handleInputChange}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={scopeOptions}
                    value={formData.scope}
                    onChange={handleScopeChange}
                    renderInput={(params) => (
                      <MDInput
                        {...params}
                        label="Scope"
                        variant="outlined"
                        fullWidth
                      />
                    )}
                    getOptionLabel={(option) => getScopeLabel(option)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={statusEventOptions}
                    value={formData.status_event}
                    onChange={handleStatusEventChange}
                    renderInput={(params) => (
                      <MDInput
                        {...params}
                        label="Status Event"
                        variant="outlined"
                        fullWidth
                      />
                    )}
                    getOptionLabel={(option) => getStatusEventLabel(option)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={["ya", "tidak"]}
                    value={formData.cek_ip_address}
                    onChange={(event, newValue) =>
                      setFormData((prev) => ({
                        ...prev,
                        cek_ip_address: newValue || "ya",
                      }))
                    }
                    renderInput={(params) => (
                      <MDInput
                        {...params}
                        label="Cek IP Address"
                        variant="outlined"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={["ya", "tidak"]}
                    value={formData.cek_koordinat}
                    onChange={(event, newValue) =>
                      setFormData((prev) => ({
                        ...prev,
                        cek_koordinat: newValue || "tidak",
                      }))
                    }
                    renderInput={(params) => (
                      <MDInput
                        {...params}
                        label="Cek Koordinat"
                        variant="outlined"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                {/* Target Dinas - hanya untuk evaluasi atau asistensi */}
                {(formData.status_event === "evaluasi" ||
                  formData.status_event === "asistensi") && (
                  <Grid item xs={12}>
                    <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                      <MDTypography variant="caption" color="text">
                        Target Dinas:
                      </MDTypography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setFormData((prev) => ({
                            ...prev,
                            target_dinas_ids: dinasListFull.map(
                              (d) => d.dinas_id
                            ),
                          }));
                        }}
                        sx={{
                          textTransform: "none",
                          fontSize: "0.75rem",
                          padding: "2px 8px",
                          color: "info.main",
                        }}
                      >
                        Pilih Semua
                      </Button>
                      {formData.target_dinas_ids.length > 0 && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() => {
                            setFormData((prev) => ({
                              ...prev,
                              target_dinas_ids: [],
                            }));
                          }}
                          sx={{
                            textTransform: "none",
                            fontSize: "0.75rem",
                            padding: "2px 8px",
                            color: "info.main",
                          }}
                        >
                          Hapus Semua
                        </Button>
                      )}
                    </MDBox>
                    <Autocomplete
                      multiple
                      options={dinasListFull.filter(
                        (d) => !formData.target_dinas_ids.includes(d.dinas_id)
                      )}
                      value={
                        dinasListFull.filter((d) =>
                          formData.target_dinas_ids.includes(d.dinas_id)
                        ) || []
                      }
                      onChange={(event, newValue) =>
                        setFormData((prev) => ({
                          ...prev,
                          target_dinas_ids: newValue.map((d) => d.dinas_id),
                        }))
                      }
                      getOptionLabel={(option) => option.nama_dinas || ""}
                      isOptionEqualToValue={(option, value) =>
                        option.dinas_id === value?.dinas_id
                      }
                      renderInput={(params) => (
                        <MDInput
                          {...params}
                          label="Target Dinas"
                          variant="outlined"
                          fullWidth
                          placeholder="Pilih dinas target"
                        />
                      )}
                      renderOption={(props, option) => (
                        <li
                          {...props}
                          key={option.dinas_id}
                          title={option.nama_dinas}
                        >
                          {truncateDinasName(option.nama_dinas)}
                        </li>
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            key={option.dinas_id}
                            label={truncateDinasName(option.nama_dinas)}
                            size="small"
                            title={option.nama_dinas}
                          />
                        ))
                      }
                    />
                  </Grid>
                )}
                {/* Detail Event Fields - hanya untuk create dan bukan evaluasi/asistensi */}
                {!editingId &&
                  formData.status_event !== "evaluasi" &&
                  formData.status_event !== "asistensi" && (
                    <>
                      <Grid item xs={12}>
                        <MDTypography variant="h6" color="text" mt={2} mb={1}>
                          Detail Event
                        </MDTypography>
                      </Grid>
                      <Grid item xs={12}>
                        <MDInput
                          label="Detail Kegiatan"
                          name="detail_kegiatan"
                          value={formData.detail_kegiatan}
                          onChange={handleDetailKegiatanChange}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Waktu Mulai"
                          type="datetime-local"
                          fullWidth
                          value={formData.waktu_mulai}
                          onChange={handleInputChange}
                          name="waktu_mulai"
                          InputLabelProps={{ shrink: true }}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Waktu Selesai"
                          type="datetime-local"
                          fullWidth
                          value={formData.waktu_finish}
                          onChange={handleInputChange}
                          name="waktu_finish"
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Autocomplete
                          freeSolo
                          options={dinasList}
                          value={formData.tempat || ""}
                          onChange={handleTempatChange}
                          renderInput={(params) => (
                            <MDInput
                              {...params}
                              label="Tempat/Lokasi"
                              variant="outlined"
                              fullWidth
                            />
                          )}
                        />
                      </Grid>
                    </>
                  )}
              </Grid>
            </MDBox>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={submitting}>
              Batal
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              color="info"
              disabled={submitting}
              startIcon={
                submitting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : null
              }
            >
              {submitting ? "Menyimpan..." : editingId ? "Update" : "Simpan"}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Dialog Form Detail Event */}
      {isAdmin && (
        <Dialog
          open={openDetailDialog}
          onClose={handleCloseDetailDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <MDTypography variant="h6">
                {editingDetailId ? "Edit Detail Event" : "Tambah Detail Event"}
              </MDTypography>
              <IconButton onClick={handleCloseDetailDialog}>
                <CloseIcon />
              </IconButton>
            </MDBox>
          </DialogTitle>
          <DialogContent>
            <MDBox pt={2} pb={3} px={3}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <MDInput
                    label="Detail Kegiatan"
                    name="detail_kegiatan"
                    value={detailFormData.detail_kegiatan}
                    onChange={handleDetailFormChange("detail_kegiatan")}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Waktu Mulai"
                    type="datetime-local"
                    fullWidth
                    value={detailFormData.waktu_mulai}
                    onChange={handleDetailFormChange("waktu_mulai")}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Waktu Selesai"
                    type="datetime-local"
                    fullWidth
                    value={detailFormData.waktu_finish}
                    onChange={handleDetailFormChange("waktu_finish")}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                {/* Pilih Dinas - hanya untuk evaluasi/asistensi */}
                {selectedEventForDetail &&
                  (selectedEventForDetail.status_event === "evaluasi" ||
                    selectedEventForDetail.status_event === "asistensi") && (
                    <Grid item xs={12}>
                      <Autocomplete
                        options={targetDinasForDetail.filter(
                          (d) => d.dinas_id && d.nama_dinas
                        )}
                        value={
                          targetDinasForDetail.find(
                            (d) => d.dinas_id === detailFormData.dinas_id
                          ) || null
                        }
                        onChange={(event, newValue) =>
                          setDetailFormData((prev) => ({
                            ...prev,
                            dinas_id: newValue ? newValue.dinas_id : null,
                          }))
                        }
                        getOptionLabel={(option) => option.nama_dinas || ""}
                        isOptionEqualToValue={(option, value) =>
                          option.dinas_id === value?.dinas_id
                        }
                        renderInput={(params) => (
                          <MDInput
                            {...params}
                            label="Pilih Dinas"
                            variant="outlined"
                            fullWidth
                            required
                            placeholder={
                              targetDinasForDetail.length === 0
                                ? "Tidak ada dinas target"
                                : "Pilih dinas dari target event"
                            }
                          />
                        )}
                        renderOption={(props, option) => (
                          <li {...props} key={option.dinas_id}>
                            {truncateDinasName(option.nama_dinas)}
                          </li>
                        )}
                        disabled={targetDinasForDetail.length === 0}
                        noOptionsText="Tidak ada dinas target untuk event ini"
                      />
                    </Grid>
                  )}
                <Grid item xs={12}>
                  <Autocomplete
                    freeSolo
                    options={dinasList}
                    value={detailFormData.tempat || ""}
                    onChange={handleDetailTempatChange}
                    renderInput={(params) => (
                      <MDInput
                        {...params}
                        label="Tempat/Lokasi"
                        variant="outlined"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </MDBox>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseDetailDialog}
              disabled={submittingDetail}
            >
              Batal
            </Button>
            <Button
              onClick={handleSubmitDetailEvent}
              variant="contained"
              color="info"
              disabled={submittingDetail}
              startIcon={
                submittingDetail ? (
                  <CircularProgress size={16} color="inherit" />
                ) : null
              }
            >
              {submittingDetail
                ? "Menyimpan..."
                : editingDetailId
                ? "Update"
                : "Simpan"}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Dialog List Detail Events */}
      <Dialog
        open={openListDetailDialog}
        onClose={handleCloseListDetailDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="h6">
              Detail Events - {selectedEventForList?.kegiatan || ""}
            </MDTypography>
            <IconButton onClick={handleCloseListDetailDialog}>
              <CloseIcon />
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={3} px={3}>
            {loadingDetailEvents ? (
              <MDBox display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </MDBox>
            ) : detailEventsList.length === 0 ? (
              <MDBox textAlign="center" py={4}>
                <MDTypography variant="body2" color="text.secondary">
                  Tidak ada detail event
                </MDTypography>
              </MDBox>
            ) : (
              <Grid container spacing={2}>
                {detailEventsList.map((detailEvent, index) => (
                  <Grid item xs={12} key={detailEvent.id}>
                    <Card>
                      <CardContent>
                        <MDBox>
                          <MDBox
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mb={2}
                          >
                            <MDTypography variant="h6" color="text">
                              Detail Event #{index + 1}
                            </MDTypography>
                          </MDBox>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <MDTypography
                                variant="caption"
                                color="text.secondary"
                              >
                                Detail Kegiatan
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {detailEvent.detail_kegiatan || "-"}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <MDTypography
                                variant="caption"
                                color="text.secondary"
                              >
                                Waktu Mulai
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {formatDateTime(detailEvent.waktu_mulai)}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <MDTypography
                                variant="caption"
                                color="text.secondary"
                              >
                                Waktu Selesai
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {formatDateTime(detailEvent.waktu_finish)}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={12}>
                              <MDTypography
                                variant="caption"
                                color="text.secondary"
                              >
                                Tempat/Lokasi
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {detailEvent.tempat || "-"}
                              </MDTypography>
                            </Grid>
                            {detailEvent.dinas_id && detailEvent.nama_dinas && (
                              <Grid item xs={12}>
                                <MDTypography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Dinas
                                </MDTypography>
                                <MDTypography variant="body2" color="text">
                                  {detailEvent.nama_dinas}
                                </MDTypography>
                              </Grid>
                            )}
                            {(detailEvent.id_pembuat ||
                              detailEvent.uid_RSS_pembuat ||
                              detailEvent.nama_pembuat) && (
                              <Grid item xs={12}>
                                <MDTypography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: "block", mb: 1 }}
                                >
                                  Pembuat Detail Event
                                </MDTypography>
                                <MDBox
                                  display="flex"
                                  alignItems="center"
                                  gap={1}
                                >
                                  {detailEvent.uid_RSS_pembuat ? (
                                    <img
                                      src={`https://smartdh.rotendaokab.go.id/uploads/${detailEvent.uid_RSS_pembuat}.jpg`}
                                      alt={
                                        detailEvent.nama_pembuat || "Pembuat"
                                      }
                                      style={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: "50%",
                                        objectFit: "cover",
                                      }}
                                      onError={(e) => {
                                        e.target.style.display = "none";
                                      }}
                                    />
                                  ) : (
                                    <MDBox
                                      display="flex"
                                      alignItems="center"
                                      justifyContent="center"
                                      sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: "50%",
                                        backgroundColor: "grey.200",
                                        color: "grey.500",
                                      }}
                                    >
                                      <PersonIcon sx={{ fontSize: 20 }} />
                                    </MDBox>
                                  )}
                                  <MDTypography variant="body2" color="text">
                                    {detailEvent.nama_pembuat || "-"}
                                  </MDTypography>
                                </MDBox>
                              </Grid>
                            )}
                          </Grid>
                        </MDBox>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseListDetailDialog}>Tutup</Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default DaftarEvents;
